import { FileCheck2, MapPin, Scale } from 'lucide-react';
import { useRef } from 'react';
import { BRAND } from '@chukua/common';
import { PoweredByChukua } from '../../../shared/features/organization/components/PoweredByChukua';
import { useOrganization } from '../../../shared/features/organization/components/OrganizationProvider';
import { TenantLogo } from '../../../shared/features/organization/components/TenantLogo';
import { useColorMode } from '../../../shared/features/theme/components/ColorModeProvider';
import { RigShowcase } from '../../reference-data/features/tyre-layouts/public/RigShowcase';
import { useRigVehicles } from '../hooks/use-rig-vehicles';

const HIGHLIGHTS = [
  { icon: MapPin, text: 'Live tracking and geofence alerts' },
  { icon: FileCheck2, text: 'eTIMS invoices straight from delivered trips' },
  { icon: Scale, text: 'Owner statements that balance to the shilling' },
];

/**
 * The brand half of the login screen, on the page colour so it follows the colour mode: cream in light, warm charcoal in dark.
 * Behind the content, the company's container rig stands on a floor with its country and its other container rigs, all on the fleet's own plates and paint, the camera flying a slow
 * loop round it; it stands in the empty frame above the footer.
 */
export function LoginBrandPanel() {
  const { configured, name, logoUrl, logoDarkUrl, countryCode } = useOrganization();
  const { mode } = useColorMode();
  const rigFrameRef = useRef<HTMLDivElement>(null);
  const vehicles = useRigVehicles();
  return (
    <aside className="relative hidden overflow-hidden bg-page p-12 text-fg lg:flex lg:flex-col lg:justify-between">
      <div aria-hidden className="absolute -top-40 -left-40 size-[36rem] rounded-full bg-primary/20 blur-3xl" />
      <RigShowcase
        livery={{ logoSrc: logoDarkUrl ?? logoUrl, name }}
        vehicles={vehicles}
        countryOutlineSrc={countryCode ? `/country-outlines/${countryCode}.json` : null}
        mode={mode}
        stillSrc={`/login-rig-${mode}.webp`}
        frameRef={rigFrameRef}
      />

      <div className="relative">
        <TenantLogo size="panel" />
      </div>

      <div className="relative max-w-md">
        <h2 className="text-4xl leading-tight font-semibold tracking-tight">Every trip, truck and shilling in one place.</h2>
        <ul className="mt-8 space-y-3">
          {HIGHLIGHTS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-sm text-fg/75">
              <span className="grid size-8 place-items-center rounded-lg bg-fg/10 ring-1 ring-fg/15">
                <Icon className="size-4" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative flex flex-col gap-6">
        <div ref={rigFrameRef} aria-hidden className="-mx-6 aspect-[16/10] xl:-mx-10" />
        <div>{configured ? <PoweredByChukua /> : <p className="font-mono text-xs text-muted">{BRAND.domain}</p>}</div>
      </div>
    </aside>
  );
}
